// src/services/api.js
// Servicio centralizado para todas las llamadas API
import { translateError } from '../i18n/errorMapper';
import { conReintentoDeRed } from '../utils/reintentoDeRed';
// ✅ Mantiene lo existente (WPPConnect, Functions, Stripe)
// ➕ Agrega WhatsApp Cloud API (Meta) [mock seguro por ahora]
// ➕ Agrega listado de Integraciones (mock) para poblar UI

// ===== CONFIGURACIÓN DE URLs =====
const API_CONFIG = {

  // Firebase Functions (HTTP v2 en us-central1)
  FIREBASE_FUNCTIONS:
    process.env.REACT_APP_FIREBASE_FUNCTIONS_URL ||
    'https://us-central1-sistema-bks.cloudfunctions.net',

  // Stripe (si usas función dedicada en Cloud Run)
  STRIPE_PAYMENT:
    process.env.REACT_APP_STRIPE_PAYMENT_URL ||
    'https://createpaymentintent-myxat54wua-uc.a.run.app',

  // Backend API Server (Baileys WhatsApp Server)
  BACKEND_API:
    process.env.REACT_APP_API_URL ||
    'https://baileys-whatsapp-server-734042103109.us-central1.run.app/api',
};

// ===== HELPER PARA MANEJAR RESPUESTAS =====

const handleResponse = async (response) => {
  if (!response.ok) {
    // Intentar parsear JSON de error; si falla, construir genérico
    const errorBody = await response.json().catch(() => ({
      error: `Error ${response.status}`,
    }));
    const rawMsg = errorBody.error || errorBody.message || `Error ${response.status}`;
    const err = new Error(translateError(rawMsg));
    // El STATUS viaja en el error. Es lo que permite distinguir «el servidor
    // contestó y dijo que no» de «nadie contestó»: `utils/reintentoDeRed.js` sólo
    // reintenta lo segundo, y sin esta marca un 401 con un mensaje desafortunado
    // podría parecer un fallo de red y reintentarse cinco veces.
    err.status = response.status;
    // Preserve errorCode from backend for specific error handling (e.g. TOKEN_INVALID)
    if (errorBody.errorCode) {
      err.errorCode = errorBody.errorCode;
    }
    // `code` es el CÓDIGO ESTABLE de los endpoints nuevos (p. ej. `invitation_invalid`).
    // Se conserva aparte de `errorCode` en vez de unificarlos: son dos convenciones que
    // ya existen en producción, y renombrar una rompería a sus llamantes en silencio.
    // La pantalla ramifica por esto — nunca por el texto, que viaja en inglés y cambia.
    if (errorBody.code) {
      err.code = errorBody.code;
    }
    throw err;
  }
  // Si es descarga (blob), este helper no se usa (ver exportaciones abajo)
  return response.json();
};

// ===== ORGANIZATION CONTEXT =====
// Stores current organization context for API calls
let currentOrgContext = null;

/**
 * Set organization context for API calls
 * This is called by OrganizationContext when context changes
 */
export const setOrgContext = (context) => {
  currentOrgContext = context;
  console.log('API org context updated:', context?.mode, context?.orgId);
};

// ===== HELPER PARA OBTENER TOKEN DE FIREBASE =====
const getAuthHeaders = async () => {
  // Importar Firebase auth dinámicamente para evitar problemas de ciclo
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  const idToken = await auth.currentUser?.getIdToken();

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${idToken}`
  };

  // Add organization context header if in organization mode
  if (currentOrgContext?.mode === 'organization' && currentOrgContext?.orgId) {
    headers['X-Org-Id'] = currentOrgContext.orgId;
  }

  return headers;
};

// ===== SERVICIOS WHATSAPP (Now via Baileys Cloud Run) =====
// All WhatsApp functionality now handled via Firebase Functions → Baileys server
// Frontend communicates with Firebase Functions, which route to Baileys backend

// ===== SERVICIOS WHATSAPP CLOUD API (Meta) =====
// 🔄 Modo configurado por variable de entorno:
// - REACT_APP_WA_CLOUD_MODE=mock (default): usa mocks locales seguros
// - REACT_APP_WA_CLOUD_MODE=proxy: usa Firebase Functions proxy real
const WA_CLOUD_MODE = process.env.REACT_APP_WA_CLOUD_MODE || 'mock';

// Implementaciones Mock (seguras, sin llamadas reales)
const waCloudMockAPI = {
  connectMeta: async () => {
    return { ok: true, message: 'Conexión simulada con Meta' };
  },
  listNumbers: async (_opts = {}) => {
    return { items: [] };
  },
  setWebhook: async ({ callbackUrl, verifyToken } = {}) => {
    return { ok: true, callbackUrl, verifyToken };
  },
  testWebhook: async () => {
    return { ok: true, sent: true };
  },
  listTemplates: async (_opts = {}) => {
    return { templates: [], fromCache: false };
  },
  sendTemplate: async ({ to, templateName, language = 'es', variables = [] } = {}) => {
    return { ok: true, to, templateName, language, variables };
  },
  getTemplates: async (_opts = {}) => {
    return { templates: [], fromCache: false };
  },
  sendTemplateMessage: async (_opts = {}) => {
    return { ok: true };
  },
  getQualityAndLimits: async (_opts = {}) => {
    return {
      quality: 'UNKNOWN',
      limitTier: 'TIER_1',
      recentLogs: [],
    };
  },
};

// Implementaciones Proxy (llaman a Firebase Functions)
const waCloudProxyAPI = {
  connectMeta: async () => {
    // Por ahora sigue siendo mock, ya que OAuth requiere configuración adicional
    return { ok: true, message: 'Conexión con Meta (requiere configuración OAuth)' };
  },
  
  listNumbers: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.FIREBASE_FUNCTIONS}/waCloud_listNumbers`, { headers });
    return handleResponse(response);
  },

  setWebhook: async ({ callbackUrl } = {}) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.FIREBASE_FUNCTIONS}/waCloud_setWebhook`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ callbackUrl }),
    });
    return handleResponse(response);
  },

  testWebhook: async () => {
    // Test local, ya que el webhook real viene de Meta
    return { ok: true, sent: true, mode: 'proxy' };
  },

  listTemplates: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.FIREBASE_FUNCTIONS}/waCloud_listTemplates`, { headers });
    return handleResponse(response);
  },

  sendTemplate: async ({ to, templateName, language = 'es', variables = [] } = {}) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.FIREBASE_FUNCTIONS}/waCloud_sendTemplate`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ to, templateName, language, variables }),
    });
    return handleResponse(response);
  },

  // Nueva función simplificada para envío directo de templates
  sendTemplateSimple: async ({ to, template, language = 'en_US' } = {}) => {
    const response = await fetch(`${API_CONFIG.FIREBASE_FUNCTIONS}/waCloud_sendSimpleTemplate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to, template, language }),
    });
    return handleResponse(response);
  },

  // Get templates from Meta via Firebase Function (with cache)
  getTemplates: async ({ phoneNumberId, wabaId, organizationId, forceRefresh } = {}) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.FIREBASE_FUNCTIONS}/getWhatsAppTemplates`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ phoneNumberId, wabaId, organizationId, forceRefresh }),
    });
    return handleResponse(response);
  },

  // Crear una plantilla en Meta desde la UI. El backend valida nombre, idioma,
  // categoría y variables ANTES de llamar a Meta, así que un 400 de aquí trae
  // `error` (código estable) y `message` (texto en claro para el usuario).
  // ⚠️ MANDA `esPromocional`, NO `categoria` (28-sep-2026). La categoría la fija el
  // SERVIDOR: no la determina el texto —medido con el mismo texto en tres WABAs— así que
  // preguntársela al cliente era prometer algo que nadie controla. Lo que sí sabe y
  // decide es si la plantilla es promocional, y eso gobierna el PIE DE BAJA.
  createTemplate: async ({ phoneNumberId, wabaId, organizationId, nombre, idioma, esPromocional, cuerpo, ejemplos } = {}) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.FIREBASE_FUNCTIONS}/createWhatsAppTemplate`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ phoneNumberId, wabaId, organizationId, nombre, idioma, esPromocional, cuerpo, ejemplos }),
    });
    return handleResponse(response);
  },

  // Send template message via Firebase Function (stores in chat)
  sendTemplateMessage: async ({ phoneNumberId, recipientPhone, templateName, languageCode, components, organizationId, chatId, templateCategory } = {}) => {
    const headers = await getAuthHeaders();
    // El backend deriva el destinatario del doc del chat (telefono || bsuid). recipientPhone
    // viaja sólo como fallback legacy; templateCategory permite la regla auth (BSUID no admite
    // plantillas de autenticación).
    const response = await fetch(`${API_CONFIG.FIREBASE_FUNCTIONS}/sendWhatsAppTemplate`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ phoneNumberId, recipientPhone, templateName, languageCode, components, organizationId, chatId, templateCategory }),
    });
    return handleResponse(response);
  },
  
  getQualityAndLimits: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.FIREBASE_FUNCTIONS}/waCloud_getQuality`, { headers });
    return handleResponse(response);
  },
};

// Selección automática según el modo configurado
export const waCloudAPI = WA_CLOUD_MODE === 'proxy' ? waCloudProxyAPI : waCloudMockAPI;

// Log del modo activo para debugging
console.log(`🔄 WhatsApp Cloud API mode: ${WA_CLOUD_MODE}`);

// ===== SERVICIOS FIREBASE FUNCTIONS =====
export const firebaseFunctions = {
  // Exportar chats (descarga archivo)
  exportarChats: async (clienteID, formato = 'csv') => {
    const response = await fetch(
      `${API_CONFIG.FIREBASE_FUNCTIONS}/exportarChats?clienteID=${clienteID}&formato=${formato}`
    );

    if (!response.ok) throw new Error('Error al exportar');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chats.${formato}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  // Exportar mensajes (descarga archivo)
  exportarMensajes: async (clienteID, chatID, formato = 'csv') => {
    const response = await fetch(
      `${API_CONFIG.FIREBASE_FUNCTIONS}/exportarMensajes?clienteID=${clienteID}&chatID=${chatID}&formato=${formato}`
    );

    if (!response.ok) throw new Error('Error al exportar mensajes');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mensajes_${chatID}.${formato}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  // Crear suscripción
  createSubscription: async (data) => {
    const response = await fetch(`${API_CONFIG.FIREBASE_FUNCTIONS}/createSubscription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  // Agregar addon
  addAddon: async (data) => {
    const response = await fetch(`${API_CONFIG.FIREBASE_FUNCTIONS}/addAddon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
};

// ===== BUSINESS METRICS API (Backend Server) =====
/**
 * Bloqueo LOCAL de un contacto — corta el bot antes de las llamadas al modelo.
 *
 * Vive aquí y no con una URL suelta en el componente a propósito: la exportación de
 * chats llegó a tener TRES deletreos distintos de su URL repartidos por el front, dos de
 * ellos muertos y apuntando a una función que no existía. Un solo sitio construye la
 * dirección de cada función.
 */
export const bloqueoAPI = {
  /**
   * ⚠️ `upstream` VIAJA, Y ESTA LÍNEA TIENE HISTORIA.
   *
   * Hasta el 21-sep-2026 esta firma no lo destructuraba y el body no lo incluía, así
   * que `ChatDetalle.js` lo calculaba bien, lo pasaba, y AQUÍ SE PERDÍA. El backend
   * recibía `undefined`, hacía `Boolean(undefined)` y registraba `upstream: false`:
   * un bloqueo puramente local.
   *
   * El efecto medido: **4 chats de WhatsApp con el banner «no puede escribirte» y
   * CERO números bloqueados en Meta** —comprobado contra `GET /block_users`, que
   * devolvió una lista vacía—. Cuatro conversaciones que el operador dejó de vigilar
   * creyendo que estaban cerradas, y de las que siguieron entrando mensajes.
   *
   * Lo peor es de dónde venía: el commit `fefd01f` (10-sep) se titulaba literalmente
   * «el botón pide el bloqueo REAL» y su comentario decía «aquí faltaba `upstream` y
   * el efecto se vio en producción». Ese arreglo se hizo en el COMPONENTE y no se
   * siguió hasta el transporte. Once días con el botón mintiendo.
   *
   * La lección es de forma, no de este campo: **un destructuring explícito es un
   * filtro silencioso**. Lo que el llamante pasa y la firma no nombra desaparece sin
   * error, sin log y sin test rojo. Medido el 21-sep sobre los 18 métodos `async` de
   * este fichero: 12 construyen un body JSON y éste era el ÚNICO que destructuraba
   * hacia un objeto literal — los demás pasan una variable o usan posicionales, y no
   * pueden perder nada. Si algún día se añade otro con esta forma, hereda la trampa.
   */
  async alternar({ chatId, organizationId, uid, bloquear, upstream }) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.FIREBASE_FUNCTIONS}/bloquearContacto`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        chatId,
        organizationId: organizationId || null,
        uid,
        bloquear,
        // Se manda SIEMPRE, también cuando es false: el backend distingue «no se pidió
        // bloqueo real» de «no se pudo», y omitirlo borraría esa diferencia.
        upstream: Boolean(upstream),
      }),
    });
    return handleResponse(response);
  },
};

export const businessMetricsAPI = {
  /**
   * Get all business metrics for a user
   * @param {string} uid - User ID
   * @param {object} options - Options { dateRange: '30d' }
   * @returns {Promise<object>} Business metrics data
   */
  async getMetrics(uid, options = {}) {
    const { dateRange = '30d' } = options;
    const headers = await getAuthHeaders();

    const response = await fetch(
      `${API_CONFIG.BACKEND_API}/business-metrics/${uid}?range=${dateRange}`,
      {
        method: 'GET',
        headers
      }
    );

    return handleResponse(response);
  },

  /**
   * Get a specific business metric
   * @param {string} uid - User ID
   * @param {string} metric - Metric name (e.g., 'avg-response-time')
   * @param {object} options - Options { dateRange: '30d' }
   * @returns {Promise<object>} Metric data
   */
  async getSpecificMetric(uid, metric, options = {}) {
    const { dateRange = '30d' } = options;
    const headers = await getAuthHeaders();

    const response = await fetch(
      `${API_CONFIG.BACKEND_API}/business-metrics/${uid}/${metric}?range=${dateRange}`,
      {
        method: 'GET',
        headers
      }
    );

    return handleResponse(response);
  }
};

// ===== METRICS API (Backend Server - System Metrics) =====
export const metricsAPI = {
  /**
   * Get system metrics overview for a user
   * Includes health stats, message counts, error rates, uptime
   * @param {string} uid - User ID (optional, uses current user if not provided)
   */
  getOverview: async (uid) => {
    const headers = await getAuthHeaders();

    // If uid provided, use user-metrics endpoint (user-authenticated)
    // Otherwise use admin metrics endpoint (requires admin access)
    const endpoint = uid
      ? `${API_CONFIG.BACKEND_API}/user-metrics/overview/${uid}`
      : `${API_CONFIG.BACKEND_API}/metrics/overview`;

    const response = await fetch(endpoint, {
      method: 'GET',
      headers,
    });
    return handleResponse(response);
  },

  /**
   * Get list of all sessions with health status
   */
  getSessions: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/metrics/sessions`, {
      method: 'GET',
      headers,
    });
    return handleResponse(response);
  },

  /**
   * Get detailed metrics for a specific session
   */
  getSessionDetails: async (uid, numeroId) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/metrics/session/${uid}/${numeroId}`, {
      method: 'GET',
      headers,
    });
    return handleResponse(response);
  },

  /**
   * Get calendar status for a user
   */
  getCalendarStatus: async (uid) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/metrics/calendar/status/${uid}`, {
      method: 'GET',
      headers,
    });
    return handleResponse(response);
  },

  /**
   * Get active alerts
   */
  getAlerts: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/metrics/alerts`, {
      method: 'GET',
      headers,
    });
    return handleResponse(response);
  },
};

// ===== SERVICIOS INTEGRACIONES (reads from Firestore) =====
export const integrationsAPI = {
  list: async () => {
    try {
      const { getAuth } = await import('firebase/auth');
      const { getFirestore, collection, getDocs } = await import('firebase/firestore');
      const auth = getAuth();
      const firestore = getFirestore();
      const uid = auth.currentUser?.uid;
      if (!uid) return [];

      // Determine path based on org context
      const basePath = currentOrgContext?.mode === 'organization' && currentOrgContext?.orgId
        ? `organizations/${currentOrgContext.orgId}/integrations`
        : `clientes/${uid}/integrations`;

      const snapshot = await getDocs(collection(firestore, basePath));
      const integrations = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        integrations.push({
          id: doc.id,
          nombre: doc.id,
          estado: data.connected ? 'conectado' : 'desconectado',
          ...data
        });
      });

      return integrations;
    } catch (error) {
      console.error('Error fetching integrations:', error);
      return [];
    }
  },
};

// ===== ORGANIZATIONS API (Backend Server) =====
export const organizationsAPI = {
  // create() ELIMINADO (21-jul-2026): una cuenta = una org. La org se crea
  // únicamente en el checkout (stripeWebhook); el endpoint POST /organizations
  // fue removido del backend. Segunda org = cuenta nueva con su checkout.

  /**
   * List all organizations the user belongs to
   * @returns {Promise<object>} List of organizations
   */
  async list() {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/organizations`, {
      method: 'GET',
      headers
    });
    return handleResponse(response);
  },

  /**
   * Get details of a specific organization
   * @param {string} orgId - Organization ID
   * @returns {Promise<object>} Organization details
   */
  async get(orgId) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/organizations/${orgId}`, {
      method: 'GET',
      headers: { ...headers, 'X-Org-Id': orgId }
    });
    return handleResponse(response);
  },

  /**
   * Update organization details
   * @param {string} orgId - Organization ID
   * @param {object} data - Data to update (e.g., { name: 'New Name' })
   * @returns {Promise<object>} Updated organization data
   */
  async update(orgId, data) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/organizations/${orgId}`, {
      method: 'PATCH',
      headers: { ...headers, 'X-Org-Id': orgId },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  /**
   * Delete an organization (soft delete)
   * @param {string} orgId - Organization ID
   * @returns {Promise<object>} Deletion confirmation
   */
  async delete(orgId) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/organizations/${orgId}`, {
      method: 'DELETE',
      headers: { ...headers, 'X-Org-Id': orgId }
    });
    return handleResponse(response);
  },

  /**
   * Invite a member to the organization
   * @param {string} orgId - Organization ID
   * @param {string} email - Member email
   * @param {string} role - Member role ('owner', 'admin', 'member', 'viewer')
   * @returns {Promise<object>} Invitation data
   */
  async inviteMember(orgId, email, role) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/organizations/${orgId}/invitations`, {
      method: 'POST',
      headers: { ...headers, 'X-Org-Id': orgId },
      body: JSON.stringify({ email, role })
    });
    return handleResponse(response);
  },

  /**
   * List all invitations for an organization
   * @param {string} orgId - Organization ID
   * @returns {Promise<object>} List of invitations
   */
  async listInvitations(orgId) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/organizations/${orgId}/invitations`, {
      method: 'GET',
      headers: { ...headers, 'X-Org-Id': orgId }
    });
    return handleResponse(response);
  },

  /**
   * Cancel a pending invitation
   * @param {string} orgId - Organization ID
   * @param {string} inviteId - Invitation ID
   * @returns {Promise<object>} Cancellation confirmation
   */
  async cancelInvitation(orgId, inviteId) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/organizations/${orgId}/invitations/${inviteId}`, {
      method: 'DELETE',
      headers: { ...headers, 'X-Org-Id': orgId }
    });
    return handleResponse(response);
  },

  /**
   * Accept an invitation (public endpoint)
   * @param {string} token - Invitation token from email
   * @returns {Promise<object>} Acceptance confirmation with organization data
   */
  async acceptInvitation(token) {
    const headers = await getAuthHeaders();
    // REINTENTO PACIENTE, y aquí sí hace falta justificarlo: reintentar un POST a ciegas
    // puede aplicarlo dos veces. Este endpoint es IDEMPOTENTE desde que un miembro ya
    // activo devuelve 200 con `code: 'already_member'` en vez de 400, así que un segundo
    // intento tras un fallo de red devuelve el mismo resultado. Sin esa mitad, esto sería
    // inseguro.
    //
    // El caso que lo pide: el enlace de invitación se abre en una pestaña nueva, o sea con
    // la app recién cargada — el momento exacto en el que un arranque en frío del servidor
    // deja la petición sin respuesta.
    const response = await conReintentoDeRed(() => fetch(`${API_CONFIG.BACKEND_API}/invitations/${token}/accept`, {
      method: 'POST',
      headers
    }));
    return handleResponse(response);
  },

  /**
   * List all members of an organization
   * @param {string} orgId - Organization ID
   * @returns {Promise<object>} List of members
   */
  async listMembers(orgId) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/organizations/${orgId}/members`, {
      method: 'GET',
      headers: { ...headers, 'X-Org-Id': orgId }
    });
    return handleResponse(response);
  },

  /**
   * Update a member's role
   * @param {string} orgId - Organization ID
   * @param {string} memberUid - Member user ID
   * @param {string} newRole - New role for the member
   * @returns {Promise<object>} Updated member data
   */
  async updateMemberRole(orgId, memberUid, newRole) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/organizations/${orgId}/members/${memberUid}`, {
      method: 'PATCH',
      headers: { ...headers, 'X-Org-Id': orgId },
      body: JSON.stringify({ role: newRole })
    });
    return handleResponse(response);
  },

  /**
   * Remove a member from the organization
   * @param {string} orgId - Organization ID
   * @param {string} memberUid - Member user ID
   * @returns {Promise<object>} Removal confirmation
   */
  async removeMember(orgId, memberUid) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/organizations/${orgId}/members/${memberUid}`, {
      method: 'DELETE',
      headers: { ...headers, 'X-Org-Id': orgId }
    });
    return handleResponse(response);
  },

  /**
   * Transfer ownership of the organization
   * @param {string} orgId - Organization ID
   * @param {string} newOwnerUid - New owner user ID
   * @returns {Promise<object>} Transfer confirmation
   */
  async transferOwnership(orgId, newOwnerUid) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/organizations/${orgId}/transfer-ownership`, {
      method: 'POST',
      headers: { ...headers, 'X-Org-Id': orgId },
      body: JSON.stringify({ newOwnerUid })
    });
    return handleResponse(response);
  },

  /**
   * Get audit log for the organization
   * @param {string} orgId - Organization ID
   * @param {number} limit - Number of entries to fetch (default: 50)
   * @returns {Promise<object>} Audit log entries
   */
  async getAuditLog(orgId, limit = 50) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/organizations/${orgId}/audit-log?limit=${limit}`, {
      method: 'GET',
      headers: { ...headers, 'X-Org-Id': orgId }
    });
    return handleResponse(response);
  },

  /**
   * Get all role configurations for the organization
   * @param {string} orgId - Organization ID
   * @returns {Promise<object>} Role configurations
   */
  async getRoles(orgId) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/organizations/${orgId}/roles`, {
      method: 'GET',
      headers: { ...headers, 'X-Org-Id': orgId }
    });
    return handleResponse(response);
  },

  /**
   * Update role configuration (owner only)
   * @param {string} orgId - Organization ID
   * @param {string} roleName - Role name ('owner', 'admin', 'member', 'viewer')
   * @param {object} updates - Updates { permissions, name, description }
   * @returns {Promise<object>} Update confirmation with affected members count
   */
  async updateRole(orgId, roleName, updates) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.BACKEND_API}/organizations/${orgId}/roles/${roleName}`, {
      method: 'PATCH',
      headers: { ...headers, 'X-Org-Id': orgId },
      body: JSON.stringify(updates)
    });
    return handleResponse(response);
  }
};

// ===== CAMPAIGNS API =====
export const campaignsAPI = {
  // Registra la DECLARACIÓN del negocio sobre sus contactos importados.
  // El backend compone el registro: aquí sólo viaja qué teléfonos, en qué idioma se
  // mostró el texto, y que la casilla se marcó. Ni el uid ni el `origen` salen de aquí.
  // EL CUERPO VIAJA ENTERO, no campo a campo. Y es un arreglo, no un estilo:
  //
  // Hasta el 31-ago-2026 esta función desestructuraba cuatro campos y serializaba
  // exactamente esos cuatro. Cuando la revisión legal añadió `finalidades`, el campo
  // se quedó aquí: `Campanas.jsx` lo pasaba, esta capa lo tiraba, y el backend
  // respondía 400 `sin_finalidades` sin que nadie en pantalla se enterara.
  //
  // Es el anti-patrón que este repo ya tiene anotado cinco veces —«lista blanca sobre
  // una respuesta de Meta = campo perdido»— pero del lado de la PETICIÓN. Una lista
  // blanca sobre el cuerpo que se envía pierde exactamente igual.
  //
  // SÓLO SE CAMBIÓ AQUÍ, a propósito. `api.js` tiene otros 12 métodos con la misma
  // forma; funcionan hoy, y tocarlos abriría doce frentes por un problema que se ha
  // manifestado en uno. Anotado en POST-LANZAMIENTO como riesgo conocido.
  //
  // El backend valida lo que le llega (`functions/declararConsentimiento.js`), así que
  // mandar el cuerpo entero no relaja ninguna comprobación: lo que no reconoce, lo
  // ignora; lo que falta, lo rechaza.
  declararConsentimiento: async (cuerpo = {}) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.FIREBASE_FUNCTIONS}/declararConsentimiento`, {
      method: 'POST',
      headers,
      body: JSON.stringify(cuerpo),
    });
    return handleResponse(response);
  },

  // Vista previa de destinatarios: cuántos quedan excluidos y por qué, ANTES de lanzar.
  // La política vive SOLO en el backend; aquí no se replica nada, se pinta lo que
  // responde. `permitidosAhora` es una foto del momento, no una promesa de envío.
  // `templateName`/`templateLanguage`/`phoneNumberId` son lo que el servidor necesita
  // para resolver la categoría por su cuenta. `templateCategory` sigue viajando como
  // TESTIGO —el servidor registra si diverge— y ya no decide nada.
  previewRecipients: async ({ organizationId, templateCategory, telefonos, templateName, templateLanguage, phoneNumberId } = {}) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.FIREBASE_FUNCTIONS}/previewCampaignRecipients`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ organizationId, templateCategory, telefonos, templateName, templateLanguage, phoneNumberId }),
    });
    return handleResponse(response);
  },

  execute: async ({ campaignId, organizationId } = {}) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.FIREBASE_FUNCTIONS}/executeCampaign`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ campaignId, organizationId }),
    });
    return handleResponse(response);
  },

  schedule: async ({ campaignId, organizationId, scheduledAt } = {}) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_CONFIG.FIREBASE_FUNCTIONS}/scheduleCampaign`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ campaignId, organizationId, scheduledAt }),
    });
    return handleResponse(response);
  },
};

// ===== EXPORTAR API =====
const api = {
  // Firebase Functions (backend principal)
  firebase: firebaseFunctions,

  // WhatsApp Cloud API (Meta)
  waCloud: waCloudAPI,

  // Business Metrics (Backend Server - Customer Facing)
  businessMetrics: businessMetricsAPI,

  // Bloqueo local de contactos
  bloqueo: bloqueoAPI,

  // System Metrics (Backend Server - Admin/Internal)
  metrics: metricsAPI,

  // Organizations (Backend Server - Multi-user Organizations)
  organizations: organizationsAPI,

  // Integraciones
  integrations: integrationsAPI,

  // Campañas
  campaigns: campaignsAPI,

  // Configuración
  config: API_CONFIG,
};

// (no cambia la API; solo opcional para consola)
if (typeof window !== 'undefined') {
  try {
    window.waCloudAPI = window.waCloudAPI || {};
    window.waCloudAPI.sendTemplateSimple = async ({ to, template, language = 'en_US' } = {}) => {
      const res = await fetch(`${API_CONFIG.FIREBASE_FUNCTIONS}/waCloud_sendSimpleTemplate`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json' },
        body: JSON.stringify({ to, template, language })
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    };
  } catch(_) {}
}

export default api;